import apiClient from '../client';
import type { CandidateAnalysisDetailV2, CandidateListAPIV2 } from '../../types';

// NOTE: `baseURL` override is a temporary workaround until the apiClient is
// refactored to use `/api` as base and embed the version in the path.
// Same caveat as api/v2/tenders.ts.
const V2_BASE = '/api/v2';

/**
 * Fetch candidates for a tender whose skill-match meets the backend's
 * threshold (currently 0.75). Returns `null` when the tender does not
 * exist (404). Any other non-2xx response is thrown by the apiClient
 * interceptor.
 */
export async function fetchCandidatesV2(
  tenderId: string,
  positionOrdinal?: number,
): Promise<CandidateListAPIV2 | null> {
  const response = await apiClient.get<CandidateListAPIV2>(
    `/tenders/${tenderId}/candidates`,
    {
      baseURL: V2_BASE,
      // When a role is selected, rank/band for that role only — this is what
      // surfaces specialists on a multi-role tender (per_position_matching_spec).
      params: positionOrdinal === undefined ? undefined : { positionOrdinal },
      validateStatus: (status) => status === 200 || status === 404,
    },
  );
  if (response.status === 404) return null;
  return response.data;
}

/**
 * Trigger analysis for one candidate against one tender. The backend fires
 * both admissibility hearings on a background thread and returns 202
 * immediately — it does NOT wait for the (tens of seconds to minutes) run to
 * finish. Poll `fetchAnalysisStatusV2` to follow it to completion.
 *
 * Resolves to `true` when the run was accepted (202), `false` when the tender
 * or candidate is unknown (404). Any other non-2xx response is thrown by the
 * apiClient interceptor.
 */
export async function runCandidateAnalysisV2(
  tenderId: string,
  candidateId: string,
): Promise<boolean> {
  const response = await apiClient.post<void>(
    `/tenders/${tenderId}/candidates/${candidateId}/analysis`,
    null,
    {
      baseURL: V2_BASE,
      validateStatus: (status) => status === 202 || status === 404,
    },
  );
  return response.status === 202;
}

/**
 * Count-derived progress of a candidate's analysis run, as reported by the
 * backend: `ruled` of `total` criteria judged, with `done` true once
 * `ruled >= total`. Poll this after a 202 from `runCandidateAnalysisV2`.
 */
export interface AnalysisStatusV2 {
  ruled: number;
  total: number;
  done: boolean;
}

/**
 * Fetch the current analysis progress for one candidate against one tender.
 * Returns `null` when the tender or candidate is unknown (404). Cheap and
 * safe to poll repeatedly.
 */
export async function fetchAnalysisStatusV2(
  tenderId: string,
  candidateId: string,
): Promise<AnalysisStatusV2 | null> {
  const response = await apiClient.get<AnalysisStatusV2>(
    `/tenders/${tenderId}/candidates/${candidateId}/analysis/status`,
    {
      baseURL: V2_BASE,
      validateStatus: (status) => status === 200 || status === 404,
    },
  );
  if (response.status === 404) return null;
  return response.data;
}

/**
 * Cross a candidate off this tender's list as not viable. The candidate is
 * still returned by `fetchCandidatesV2` flagged `removed:true`, so the UI can
 * list them as restore-able. Idempotent server-side. Resolves to `true` on a
 * 204, `false` on a 404 (unknown tender/candidate).
 */
export async function removeCandidateV2(
  tenderId: string,
  candidateId: string,
): Promise<boolean> {
  const response = await apiClient.post<void>(
    `/tenders/${tenderId}/candidates/${candidateId}/removal`,
    null,
    {
      baseURL: V2_BASE,
      validateStatus: (status) => status === 204 || status === 404,
    },
  );
  return response.status === 204;
}

/**
 * Restore a previously-removed candidate to this tender's list. Idempotent
 * server-side. Resolves to `true` on a 204, `false` on a 404.
 */
export async function restoreCandidateV2(
  tenderId: string,
  candidateId: string,
): Promise<boolean> {
  const response = await apiClient.delete<void>(
    `/tenders/${tenderId}/candidates/${candidateId}/removal`,
    {
      baseURL: V2_BASE,
      validateStatus: (status) => status === 204 || status === 404,
    },
  );
  return response.status === 204;
}

/**
 * Fetch the full per-criterion analysis for one candidate against one tender
 * — the data behind the candidate analysis page. Every
 * requirement/responsibility criterion is a row; unruled ones carry a null
 * verdict. Returns `null` on 404 (unknown tender/candidate).
 */
export async function fetchCandidateAnalysisDetailV2(
  tenderId: string,
  candidateId: string,
): Promise<CandidateAnalysisDetailV2 | null> {
  const response = await apiClient.get<CandidateAnalysisDetailV2>(
    `/tenders/${tenderId}/candidates/${candidateId}/analysis`,
    {
      baseURL: V2_BASE,
      validateStatus: (status) => status === 200 || status === 404,
    },
  );
  if (response.status === 404) return null;
  return response.data;
}
